import { tick } from "svelte";
import { lock, unlock } from "tua-body-scroll-lock";
import { Enter } from "../keys";
import { unify } from "./index";
import { keydown } from "./keyboard";

export function focusable(node: HTMLElement, options: { role: string } = { role: "button" }) {
  node.tabIndex = 0;
  node["role"] = options.role;

  // listen to enter for trigger click
  return keydown(node, (e: KeyboardEvent) => {
    if (e.key == Enter) {
      node.dispatchEvent(new MouseEvent("click"));
    }
  });
}

export function returnFocus(_: Element) {
  let previouslyFocusedElement = document.activeElement;

  return {
    destroy() {
      if (previouslyFocusedElement && previouslyFocusedElement instanceof HTMLElement) {
        previouslyFocusedElement.focus();
      }
    },
  };
}

export function focusTrap(node: HTMLElement) {
  let firstFocusableEl: HTMLElement;
  let lastFocusableEl: HTMLElement;

  const getVisibleFocusableElements = () =>
    Array.from(
      node.querySelectorAll<HTMLElement>(
        'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"]), iframe',
      ),
    ).filter(
      (el) =>
        !el.hasAttribute("disabled") &&
        !el["hidden"] &&
        el["offsetParent"] !== null &&
        el.tabIndex > -1,
    );

  const updateFocusableElements = () => {
    const focusableElements = getVisibleFocusableElements();
    firstFocusableEl = focusableElements[0];
    lastFocusableEl = focusableElements[focusableElements.length - 1];
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Tab") {
      if (event.shiftKey) {
        /* shift + tab */
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          event.preventDefault();
        }
      } /* tab */ else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          event.preventDefault();
        }
      }
    }
    if (!node.contains(event.target as Node)) {
      event.preventDefault();
      firstFocusableEl?.focus();
    }
  };

  const observeChanges = new MutationObserver(updateFocusableElements);

  updateFocusableElements();
  tick().then(() => {
    firstFocusableEl?.focus();
  });

  observeChanges.observe(node, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["hidden", "tabindex", "disabled"],
  });

  return unify([
    keydown(node.ownerDocument, handleKeyDown),
    {
      destroy: () => {
        return observeChanges.disconnect();
      },
    },
  ]);
}

export function scrollLock(n: HTMLElement) {
  lock(n);
  return {
    destroy() {
      unlock(n);
    },
  };
}
